import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

// Lean cross-shell account facade: one GET /api/portfolio/summary (read-only,
// no row minting, no passcode — never the editor view) supplies the identity
// header (display name + avatar, the gallery-domain user_profiles projection)
// and the portfolio publish state the /home onboarding prompt needs. The email
// is not here — it rides in from /api/users/me/profile via GalleryIdentityData.
interface PortfolioSummaryWire {
  displayName?: string | null;
  avatarUrl?: string | null;
  handle?: string | null;
  isPublished?: boolean | null;
}

export interface AccountSummary {
  displayName: string | null;
  avatarUrl: string | null;
  handle: string | null;
  portfolioPublished: boolean | null;
}

@Injectable({ providedIn: 'root' })
export class AccountService {
  private readonly http = inject(HttpClient);

  private readonly summarySignal = signal<AccountSummary | null>(null);
  private inFlight: Promise<void> | null = null;

  readonly summary = this.summarySignal.asReadonly();

  /** Idempotent: concurrent callers share one request; a resolved summary is kept. */
  load(): Promise<void> {
    if (this.summarySignal() !== null) return Promise.resolve();
    return this.refresh();
  }

  /**
   * Re-reads the summary even when one is held: the handle is edited on the account screen,
   * which does not know this facade, so a screen that acts on the handle (the gallery address)
   * asks for a fresh summary when it opens. The held summary stays on screen until the answer
   * lands; concurrent callers still share one request.
   */
  refresh(): Promise<void> {
    this.inFlight ??= this.fetch().finally(() => (this.inFlight = null));
    return this.inFlight;
  }

  private async fetch(): Promise<void> {
    try {
      const wire = await firstValueFrom(this.http.get<PortfolioSummaryWire>('/api/portfolio/summary'));
      this.summarySignal.set({
        displayName: wire.displayName ?? null,
        avatarUrl: wire.avatarUrl ?? null,
        handle: wire.handle ?? null,
        portfolioPublished: wire.isPublished ?? null,
      });
    } catch {
      // Degrade silently: the menu falls back to the email line, the home
      // prompt simply doesn't render. Next load() retries.
    }
  }
}
